import { useState } from 'react';
import { consoleApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { withConsoleBase } from '../config';
import type { MailStatus } from '../types';

export function GoogleMailConnection({ config }: { config: MailStatus }) {
  const { csrfToken, capabilities } = useAuth();
  const cache = useQueryClient();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState(false);
  const callback = window.location.origin + withConsoleBase('/mail/google/callback');
  const connected = new URLSearchParams(window.location.search).get('google') === 'connected' && config.configured;
  async function act(save: boolean) {
    setBusy(true); setMessage(''); setFailed(false);
    try {
      if (save) {
        await consoleApi.mailGoogleClient(clientId.trim(), clientSecret.trim(), csrfToken);
        setClientId('');
        setMessage('Google app saved. You can now connect the mailbox.');
        await cache.invalidateQueries({ queryKey: ['mail', 'status'] });
      } else {
        const result = await consoleApi.mailGoogleStart(csrfToken);
        const target = new URL(result.authorization_url);
        if (target.origin !== 'https://accounts.google.com' || target.pathname !== '/o/oauth2/v2/auth') throw new Error('The Google authorization link could not be verified.');
        window.location.assign(target.href);
      }
    } catch (error) {
      setFailed(true); setMessage(error instanceof Error ? error.message : 'The connection could not complete. Please try again.');
    } finally { setClientSecret(''); setBusy(false); }
  }
  return <section className="panel google-mail-connection">
    <h2>Connect Google mailbox</h2>
    <p>Authorize {config.mailbox} in your browser. Every agent reply still needs Director approval.</p>
    {connected ? <p role="status" className="inline-alert">Google mailbox connected. Processing and sending remain paused until enabled.</p> : null}
    {!config.oauth_ready ? <p role="status">Secure mailbox storage is being configured on the server.</p> : <>
      {!config.oauth_client_configured ? <p>First, register the GTM app with Google using the setup below. You only need to do this once.</p> : null}
      <details open={!config.oauth_client_configured}>
        <summary>{config.oauth_client_configured ? 'Update Google app settings' : 'One-time Google app setup'}</summary>
        <ol>
          <li>In <a href="https://console.cloud.google.com/auth/clients" target="_blank" rel="noreferrer">Google Cloud → Google Auth Platform → Clients</a>, create an OAuth client with application type <strong>Web application</strong>. Use your LiquidEdge project, enable Gmail API, and select an Internal audience.</li>
          <li>Add this exact <strong>Authorized redirect URI</strong>:<input aria-label="Authorized redirect URI" readOnly value={callback} onFocus={event => event.target.select()} /></li>
          <li>Copy the client ID and client secret into the fields below. No download or installation is needed.</li>
        </ol>
        <form onSubmit={event => { event.preventDefault(); void act(true); }} autoComplete="off">
          <label>Google client ID<input required value={clientId} onChange={event => setClientId(event.target.value)} maxLength={240} placeholder="…apps.googleusercontent.com" autoComplete="off" /></label>
          <label>Google client secret<input required type="password" value={clientSecret} onChange={event => setClientSecret(event.target.value)} minLength={10} maxLength={256} autoComplete="new-password" /></label>
          <p>The secret is stored securely and is never shown again. Pause the mailbox before changing these settings.</p>
          <button className="button button--quiet" disabled={busy || !config.paused || !capabilities.runAgents}>Save Google app</button>
        </form>
      </details>
      <button type="button" className="button button--primary" disabled={busy || !config.oauth_client_configured || !config.paused || !capabilities.runAgents} onClick={() => void act(false)}>{busy ? 'Connecting…' : config.configured ? 'Reconnect Google mailbox' : 'Connect Google mailbox'}</button>
      {!config.paused ? <p>Pause the mailbox before reconnecting.</p> : null}
    </>}
    {message ? <p role={failed ? 'alert' : 'status'} className={'inline-alert' + (failed ? ' inline-alert--error' : '')}>{message}</p> : null}
  </section>;
}
